import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { select, Store } from '@ngrx/store';
import { Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, mergeMap } from 'rxjs/operators';
import { AccountActions } from '../actions';
import { RedirectionService } from '../services/redirection.service';
import { TokenService } from '../services/token.service';
import { AccountHasDomainGuard } from './base/account-has-domain.guard';
import { AdminGuard } from './base/admin.guard';
import { DomainRedirectionGuard } from './base/domain-redirection.guard';
import { DomainSigninRedirectionGuard } from './base/domain-signin-redirection.guard';
import { IsAccountValidGuard } from './base/is-account-valid.guard';
import { IsSubscriptionValidGuard } from './base/is-subscription-valid.guard';
import { LoadDomainGuard } from './base/load-domain.guard';
import { LoadFreemiumUploaderCampaignGuard } from './base/load-freemium-uploader-campaign.guard';
import { LoadRolesAndTeamsGuard } from './base/load-roles-and-teams.guard';
import { LoadServicesGuard } from './base/load-services.guard';
import { isPlatformBrowser } from '@angular/common';
import { SEOService } from '../services/seo.service';

@Injectable({
  providedIn: 'root'
})
export class UploaderGuard {

  constructor(
    private loadDomainGuard: LoadDomainGuard,
    private loadServicesGuard: LoadServicesGuard,
    private loadFreemiumUploaderCampaignGuard: LoadFreemiumUploaderCampaignGuard,
    private isAccountValidGuard: IsAccountValidGuard,
    private adminGuard: AdminGuard,
    private domainRedirectionGuard: DomainRedirectionGuard,
    private userHasTeam: AccountHasDomainGuard,
    private loadRolesAndTeamsGuard: LoadRolesAndTeamsGuard,
    private domainSigninRedirectionGuard: DomainSigninRedirectionGuard,
    private isSubscriptionValid: IsSubscriptionValidGuard,
    private tokenService: TokenService,
    private redirectionService: RedirectionService,
    private router: Router,
    private store$: Store<fromRoot.State>,
    private seoService: SEOService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  canActivate(): Observable<boolean> {
    if (isPlatformBrowser(this.platformId)) {
      return this.loadDomainGuard.canActivate().pipe(mergeMap((domainLoaded: boolean) => {
        if (domainLoaded) {
          return this.loadServicesGuard.canActivate().pipe(mergeMap((servicesLoaded: boolean) => {
            if (servicesLoaded) {
              return this.isAccountValidGuard.canActivate().pipe(mergeMap((isAccountValid: boolean) => {
                if (isAccountValid) {
                  return this.isAccountPremium().pipe(mergeMap((isLoggedIn: boolean) => {
                    if (isLoggedIn) {
                      return this.loadRolesAndTeamsGuard.canActivate().pipe(mergeMap((rolesAndTeamsLoaded: boolean) => {
                        if (rolesAndTeamsLoaded) {
                          return this.adminGuard.canActivate().pipe(mergeMap((isAdmin: boolean) => {
                            if (isAdmin) {
                              return this.isSubscriptionValid.canActivate().pipe(mergeMap((isSubscriptionValid: boolean) => {
                                if (isSubscriptionValid) {
                                  return this.userHasTeam.canActivate().pipe(mergeMap((userHasTeam: boolean) => {
                                    if (userHasTeam) {
                                      return this.domainRedirectionGuard.canActivate();
                                    } else {
                                      this.redirectionService.redirectToSignupDomain();
                                    }
                                  }));
                                } else {
                                  return of(false);
                                }
                              }));
                            } else {
                              return this.userHasTeam.canActivate().pipe(mergeMap((userHasTeam: boolean) => {
                                if (userHasTeam) {
                                  return this.domainRedirectionGuard.canActivate();
                                } else {
                                  this.redirectionService.redirectToInvalidConfiguration();
                                }
                              }));
                            }
                          }));
                        }
                      }));
                    } else {
                      return this.domainSigninRedirectionGuard.canActivate().pipe(mergeMap((stayHere: boolean) => {
                        if (stayHere) {
                          return this.domainRedirectionGuard.canActivate().pipe(mergeMap((domainChecked: boolean) => {
                            if (domainChecked) {
                              return this.loadFreemiumUploaderCampaignGuard.canActivate().pipe(mergeMap((campaignLoaded: boolean) => {
                                if (campaignLoaded) {
                                  return of(campaignLoaded);
                                }
                              }));
                            } else {
                              return of(domainChecked);
                            }
                          }));
                        } else {
                          return of(stayHere);
                        }
                      }));
                    }
                  }));
                } else {
                  return of(false);
                }
              }));
            }
          }));
        }
      }));

    } else {
      this.seoService.setMetasLanding();
      return of(true);
    }
  }

  isAccountPremium(): Observable<boolean> {
    const isAccountPremium$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      select(fromRoot.getToken),
      distinctUntilChanged(),
    ).subscribe((token: any) => {
      if (token) {
        if (this.tokenService.isAnonymousAccount(token)) {
          isAccountPremium$.next(false);
        } else {
          if (this.tokenService.isTokenExpired(token)) {
            this.store$.dispatch(AccountActions.refreshToken());
          } else {
            isAccountPremium$.next(true);
          }
        }
      } else {
        this.router.navigateByUrl('/signin');
        isAccountPremium$.next(false);
      }
    });
    return isAccountPremium$.asObservable();
  }
}
